'use client'

import * as React from 'react'

import { cn } from '../../lib'

type ProgressVariant = 'green-500' | 'blue' | 'red' | 'gray-400'

interface ProgressProps {
  value: number
  color?: ProgressVariant | string
  className?: string
  showIndicator?: boolean
  ref?: React.Ref<HTMLDivElement>
  progressRef?: React.Ref<HTMLDivElement>
  indicatorRef?: React.Ref<HTMLDivElement>
}

const Progress = ({
  className,
  value = 0,
  color = 'green-500',
  showIndicator = true,
  ref,
  progressRef,
  indicatorRef,
  ...props
}: React.HTMLAttributes<HTMLDivElement> & ProgressProps) => {
  const getBackgroundColor = (color: ProgressVariant | string) => {
    switch (color) {
      case 'green-500':
        return 'bg-green-500'
      case 'blue':
        return 'bg-[#6184FF]'
      case 'red':
        return 'bg-[#FD6771]'
      case 'gray-400':
        return 'bg-gray-400'
      default:
        return `bg-${color}`
    }
  }

  const getBorderColor = (color: ProgressVariant | string) => {
    switch (color) {
      case 'green-500':
        return 'border-green-500'
      case 'blue':
        return 'border-[#6184FF]'
      case 'red':
        return 'border-[#FD6771]'
      case 'gray-400':
        return 'border-gray-400'
      default:
        return `border-${color}`
    }
  }

  const clampedValue = Math.max(0, Math.min(100, value))

  return (
    <div
      ref={ref}
      className={cn('relative flex-start flex h-2 w-full rounded-2xl bg-gray-200 gap-px', className)}
      {...props}
    >
      <div
        className={cn('h-2 rounded-2xl overflow-hidden', getBackgroundColor(color))}
        style={{ width: `${clampedValue}%` }}
        ref={progressRef}
      />
      {showIndicator && (
        <div
          className={cn(
            'absolute top-1/2 -translate-y-1/2 w-4 h-4 rounded-full border-[2px] shadow-sm bg-white',
            getBorderColor(color),
          )}
          style={{
            left: `calc(${clampedValue}% - 8px)`,
          }}
          ref={indicatorRef}
        />
      )}
    </div>
  )
}

Progress.displayName = 'Progress'

export { Progress, type ProgressProps, type ProgressVariant }
